<script setup lang="ts">
import packageInfo from "~/package.json";
import {
  Ibar,
  Footer,
  SnackbarManager,
  type ITabItemProps
} from "@frontiers-vue3/public-pages";
import { addPackageToWindowFrontiers } from "~/helpers";
import { Tabs } from "@frontiers-vue3/public-pages";
import { useJournalPagesService } from "~/composables/useJournalPagesService";

addPackageToWindowFrontiers(packageInfo);
const {
  showRtImpactTab,
  isImpactMetricsVisible,
  isAboutThisResearchTopicsVisible
} = useRuntimeConfig().public;
const terminology = useTerminologyStore();
const store = useMain();
const route = useRoute();

useHead({
  title: `${useSanitizeHtml(store.currentResearchTopic.title, {
    ALLOWED_TAGS: ["sub", "sup"]
  })} | ${terminology.getTerm("tenantName")} ${terminology.getTerm(
    "researchTopic"
  )}`
});

onBeforeMount(() => {
  store.fetchTotals().then(() => {
    tabArticle.value.counter = store.totalArticles;
    tabAuthors.value.counter = store.totalAuthors;
  });
});

const rtDetails = store.currentResearchTopic;

const selectedTab = ref<number>(0);

const tabArticle = ref<ITabItemProps>({
  label: terminology.getTerm("articles"),
  counter: store.totalArticles
});
const tabAuthors = ref<ITabItemProps>({
  label: terminology.getTerm("authors"),
  counter: store.totalAuthors
});

const tabs = [tabArticle.value, tabAuthors.value];

const tabSelected = (): void => {
  const tabValue = tabs[selectedTab.value].label.toLowerCase();
  if (tabValue !== "overview") {
    navigateTo(
      `/research-topics/${route.params.rtId}/${rtDetails?.slug}/${tabs[
        selectedTab.value
      ].label.toLowerCase()}`
    );
  } else {
    navigateTo(`/research-topics/${route.params.rtId}/${rtDetails?.slug}`);
  }
};

const impactNumbers: Record<string, number> = {
  [`${terminology.getTerm("topicTitle")} views`]:
    store.currentResearchTopic.impactV5?.views || 0,
  [`${terminology.getTerm("article")} views`]:
    store.currentResearchTopic.impactV5?.articleViews || 0,
  [`${terminology.getTerm("article")} downloads`]:
    store.currentResearchTopic.impactV5?.downloads || 0
};

const updateSelectedTabIndex = (): void => {
  const rtTab = (route.name as string).split("research-topics-rtId-rtSlug-")[1];

  if (!rtTab) {
    selectedTab.value = 0;
    return;
  }

  if (rtTab === "Impact" && !showRtImpactTab) {
    selectedTab.value = 0;
    tabSelected();
    return;
  }
  route;

  const index = tabs.findIndex(
    (tab) => tab.label.toLowerCase() === rtTab.toLowerCase()
  );

  selectedTab.value = index >= 0 ? index : 0;
};

if (
  isImpactMetricsVisible &&
  showRtImpactTab &&
  !tabs.find((tab) => tab.label === "Impact")
) {
  tabs.push({ label: "Impact", counter: 0 });
}

if (
  isAboutThisResearchTopicsVisible &&
  !tabs.find((tab) => tab.label === "Overview")
) {
  tabs.unshift({
    label: "Overview",
    counter: 0
  });
}

updateSelectedTabIndex();

watch(
  () => route.path,
  () => updateSelectedTabIndex()
);
const { data } = useAsyncData("ibar-footer", async () => {
  const { getIbarComponent, getFooterComponent } = useJournalPagesService();
  const ibar = await getIbarComponent(store.getPrimaryJournalId);
  const footer = await getFooterComponent();
  return { ibar, footer };
});
</script>
<template>
  <div :theme="store.colorPalette">
    <Ibar v-if="data" v-bind="data.ibar" about-journal-text="About journal" />

    <MainHeader
      :is-submit-manuscript-enabled="store.getIsSubmitManuscriptEnabled" />

    <Tabs
      id="MainTabs"
      v-model="selectedTab"
      :tabs="tabs"
      size="md"
      :hide-divider="true"
      @update:model-value="tabSelected">
      <template #panel="panelProps">
        <div
          v-bind="{ ...panelProps }"
          :class="`content content-${tabs[selectedTab].label}`">
          <div class="content__main">
            <NuxtPage />
          </div>
          <RTSidePanel
            :impact-numbers="impactNumbers"
            :is-submit-manuscript-enabled="
              store.getIsSubmitManuscriptEnabled
            " />
        </div>
      </template>
    </Tabs>

    <Footer v-if="data" v-bind="data.footer" />
    <SnackbarManager />
  </div>
</template>
<style lang="scss">
.content {
  display: flex;
  position: relative;
  justify-content: space-between;
  padding: 48px 0;
  @include generalPageWrapper;

  @media #{$break-md} {
    flex-wrap: wrap;
  }

  @media #{$break-sm} {
    width: var(--layout-col-21);
  }

  @media #{$break-xs} {
    padding: 40px 0;
    width: var(--layout-col-24);
  }

  &__main {
    flex: 1;
    width: 100%;
  }
}
.Tabs {
  &__scrollWrapper {
    background-color: $white;
    border-bottom: 0.0625rem solid $grey40;
  }
  &__slidingPanel {
    width: var(--layout-col-20);
    margin-left: auto;
    margin-right: auto;

    @media #{$break-sm} {
      width: var(--layout-col-21);
    }

    @media #{$break-xs} {
      width: var(--layout-col-24);
    }
  }
  &__tablist {
    @include generalPageWrapper;
  }
}
.SnackbarManager {
  @media #{$break-md} {
    bottom: 32px;
  }

  @media #{$break-xs} {
    bottom: 16px;
  }
}
</style>
